import { useMemo } from "react"
import { Users, FolderOpen, Link2, FileText, HardDrive } from "lucide-react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"

import { Card, CardContent, CardTitle } from "@f2e/ui/components/base"

import {
  DailyOrganizationMetricsData,
  OrganizationStatsData,
} from "@/types/metrics"

interface OrganizationMetricsTableProps {
  dailyMetrics: DailyOrganizationMetricsData[]
  monthlyMetrics: DailyOrganizationMetricsData[]
  stats: OrganizationStatsData
}

export const OrganizationMetricsTable = ({
  dailyMetrics,
  monthlyMetrics,
  stats,
}: OrganizationMetricsTableProps) => {
  const { t } = useTranslation()

  // Sort daily metrics by date (newest first)
  const sortedDailyMetrics = useMemo(() => {
    return [...dailyMetrics].sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })
  }, [dailyMetrics])

  // Calculate date range for display
  const dateRange = useMemo(() => {
    if (dailyMetrics.length === 0) {
      return null
    }

    const dates = dailyMetrics.map((m) => new Date(m.date).getTime())
    const startDate = new Date(Math.min(...dates))
    const endDate = new Date(Math.max(...dates))

    return {
      start: format(startDate, "yyyy-MM-dd"),
      end: format(endDate, "yyyy-MM-dd"),
    }
  }, [dailyMetrics])

  const formatBytes = (bytes: number) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"]
    if (bytes === 0) return "0 Byte"
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${Math.round(bytes / Math.pow(1024, i))} ${sizes[i]}`
  }

  // Stats card data with icons and colors - now with translated titles
  const statsCards = useMemo(
    () => [
      {
        title: t("metrics.organization.stats.users"),
        value: stats.userCount,
        icon: Users,
        color: "text-blue-500",
        bgColor: "bg-blue-50 dark:bg-blue-950/30",
      },
      {
        title: t("metrics.organization.stats.workspaces"),
        value: stats.workspaceCount,
        icon: FolderOpen,
        color: "text-purple-500",
        bgColor: "bg-purple-50 dark:bg-purple-950/30",
      },
      {
        title: t("metrics.organization.stats.connections"),
        value: stats.connectionCount,
        icon: Link2,
        color: "text-green-500",
        bgColor: "bg-green-50 dark:bg-green-950/30",
      },
      {
        title: t("metrics.organization.stats.contextObjects"),
        value: stats.contextObjectCount,
        icon: FileText,
        color: "text-amber-500",
        bgColor: "bg-amber-50 dark:bg-amber-950/30",
      },
      {
        title: t("metrics.organization.stats.storage"),
        value: formatBytes(stats.contextSize),
        icon: HardDrive,
        color: "text-rose-500",
        bgColor: "bg-rose-50 dark:bg-rose-950/30",
        isFormatted: true,
      },
    ],
    [t, stats]
  )

  // Common table header and cell classes for consistency
  const tableHeaderClass =
    "h-10 px-2 text-left align-middle font-medium text-muted-foreground bg-muted/50"
  const tableHeaderRightClass =
    "h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-muted/50"
  const tableCellClass = "p-2 align-middle"
  const tableCellRightClass = "p-2 text-right align-middle"

  return (
    <div className="space-y-8">
      {/* Stats Cards - Moved to top for better visibility */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
        {statsCards.map((card, index) => (
          <Card
            key={index}
            className="border shadow-xs hover:shadow-md transition-all duration-200"
          >
            <CardContent className="p-6">
              <div className="flex items-start">
                <div className={`p-3 rounded-lg ${card.bgColor} mr-4`}>
                  <card.icon className={`h-6 w-6 ${card.color}`} />
                </div>
                <div className="flex flex-col">
                  <CardTitle className="text-sm font-medium text-muted-foreground mb-1">
                    {card.title}
                  </CardTitle>
                  <div className="flex items-end">
                    <span className="text-2xl font-bold">{card.value}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Daily Metrics Table */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {t("metrics.organization.daily.title")}
          </h3>
          {dateRange && (
            <div className="text-sm text-muted-foreground">
              {t("metrics.dateRangeDescription", {
                startDate: dateRange.start,
                endDate: dateRange.end,
              })}
            </div>
          )}
        </div>

        <div className="border rounded-md shadow-xs overflow-hidden">
          <div className="relative">
            <table className="w-full">
              <thead>
                <tr>
                  <th style={{ width: "120px" }} className={tableHeaderClass}>
                    {t("metrics.date")}
                  </th>
                  <th
                    style={{ width: "120px" }}
                    className={tableHeaderRightClass}
                  >
                    {t("metrics.organization.conversations")}
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          <div className="max-h-[30vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            <table className="w-full">
              <tbody>
                {sortedDailyMetrics.map((metric, index) => (
                  <tr
                    key={metric.date}
                    className={`border-t border-gray-100 dark:border-gray-800 hover:bg-muted/30 transition-colors ${
                      index % 2 === 1 ? "bg-gray-50 dark:bg-gray-800/30" : ""
                    }`}
                  >
                    <td style={{ width: "120px" }} className={tableCellClass}>
                      {format(new Date(metric.date), "yyyy-MM-dd")}
                    </td>
                    <td
                      style={{ width: "120px" }}
                      className={tableCellRightClass}
                    >
                      {metric.conversationsSubmitted}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Monthly Aggregation Table */}
      <div>
        <h3 className="text-lg font-medium mb-4">
          {t("metrics.organization.monthly.title")}
        </h3>
        <div className="border rounded-md shadow-xs overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                <th className={tableHeaderClass}>{t("metrics.month")}</th>
                <th className={tableHeaderRightClass}>
                  {t("metrics.organization.conversations")}
                </th>
              </tr>
            </thead>
            <tbody>
              {monthlyMetrics.map((metric, index) => (
                <tr
                  key={metric.date}
                  className={`border-t border-gray-100 dark:border-gray-800 hover:bg-muted/30 transition-colors ${
                    index % 2 === 1 ? "bg-gray-50 dark:bg-gray-800/30" : ""
                  }`}
                >
                  <td className={tableCellClass}>
                    {format(new Date(metric.date), "yyyy-MM")}
                  </td>
                  <td className={tableCellRightClass}>
                    {metric.conversationsSubmitted}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
